import React from 'react';
import { useTranslation } from 'react-i18next';
import { ensureNamespaces } from '../i18n';

interface NamespacesProps {
  namespaces: readonly string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Holds back `children` until every namespace it needs is in i18next.
 *
 * Without this, a route reached by client-side navigation would render one
 * frame with raw keys (`audit.title`) before its dictionary file arrived:
 * i18next returns the key itself for an unknown namespace, and React commits
 * that frame. Blocking here is safe because the page's own chunk is loading
 * over the same connection anyway, and the reserved space is already in place.
 */
export function Namespaces({ namespaces, children, fallback = null }: NamespacesProps) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  // A new array identity on every render would refetch forever; the generated
  // map returns a stable reference, so the join is the real cache key.
  const key = namespaces.join('|');

  const [ready, setReady] = React.useState(false);

  // Reset ready when the namespace set changes (client-side route navigation).
  // React Router reuses the same <Namespaces> instance for every route (same
  // component type at the same tree position), so `ready` stays `true` from
  // the previous page. Without this reset, children render immediately with
  // raw translation keys before the new namespaces have loaded — the exact
  // bug visible on /audit when navigating from the homepage.
  const [activeKey, setActiveKey] = React.useState(key);
  if (key !== activeKey) {
    setActiveKey(key);
    setReady(false);
  }

  React.useEffect(() => {
    let cancelled = false;
    if (!key) {
      setReady(true);
      return;
    }
    void ensureNamespaces(lang, key.split('|')).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [key, lang]);

  if (!ready) return <>{fallback}</>;
  return <>{children}</>;
}
